import { AttendState } from 'components/common/AttendState';
import { CloseButton, Dropdown, DropdownButton, Table } from 'react-bootstrap';
import React, { useState, useEffect, useMemo } from 'react';
import { Sidebar } from 'components/shared/sidebar';
import { useMutation, useQueries } from '@tanstack/react-query';
import { patchDataQuery } from 'api/patchQuery';
import { getDataQuery } from 'api';
import useSsoRouting from 'hooks/common/useSsoRouting';
import { getLectureQuery } from 'api/getLectureQuery';
import { IsDone, IsPass } from 'components/common';

const OnlineAttendancePopup = ({
  setVisiblePopup,
  userNumber,
  userLmsNumber,
  userName,
  courseId,
  type,
  totalScore,
  finalGrade,
  attendanceCnt,
  absenceCnt,
}) => {
  console.log('courseId: ', courseId);
  console.log('userNumber: ', userNumber);
  const studentSubjectApis = useQueries({
    queries: [
      {
        // 학생 - [출결현황] 출결 데이터
        queryKey: ['studentOnlineAttendanceList', userNumber, courseId],
        queryFn: () => getDataQuery(`/attendance/course/${courseId}/student/online/detail?studentId=${userNumber}`),
        enabled: !!courseId,
      },
    ],
  });

  const [studentAttendanceList] = studentSubjectApis?.map((query) => query?.data?.data);

  const [refetchAttendanceList] = studentSubjectApis?.map((query) => query?.refetch) || [];

  const closePopup = () => {
    setVisiblePopup(false);
  };

  const [updatedAttendanceStates, setUpdatedAttendanceStates] = useState([]);

  useEffect(() => {
    if (studentAttendanceList) {
      setUpdatedAttendanceStates(studentAttendanceList);
    }
  }, [studentAttendanceList]);

  // 정렬 상태 (어떤 컬럼, 어떤 방향)
  const [sortConfig, setSortConfig] = useState({
    key: 'moduleNumber', // 'moduleNumber' | 'title' | 'category' | 'submittedAt' | 'onlineAttendanceState' | 'pointsPossible' | 'score'
    direction: 'asc', // 'asc' | 'desc'
  });
  const mutation = useMutation({
    mutationFn: (body) => patchDataQuery('/attendance', body),
    onSuccess: () => {},
    onError: (error) => {
      console.error(error);
    },
  });

  const { ssoRouting } = useSsoRouting();
  // 온라인 강의실
  const handleGoingLecture = async (url) => {
    console.log('url: ', url);
    ssoRouting(url);
    const { data: getCanvasUrl } = await getLectureQuery('');

    if (getCanvasUrl?.errorCode) return;
    window.open(getCanvasUrl);
  };

  const handleStatusChange = (moduleNumber, contentNumber, newState) => {
    setUpdatedAttendanceStates((prev) => {
      if (!prev || prev.length === 0) return prev;

      const copy = prev.map((record) => ({
        ...record,
        onlineAttendanceDetailDto: record.onlineAttendanceDetailDto.map((item) => ({ ...item })),
      }));

      const moduleIdx = copy.findIndex((r) => r.moduleNumber === moduleNumber);
      if (moduleIdx === -1) return prev;

      const itemIdx = copy[moduleIdx].onlineAttendanceDetailDto.findIndex(
        (item) => item.contentNumber === contentNumber
      );
      if (itemIdx === -1) return prev;

      const targetRecord = copy[moduleIdx];
      const targetItem = targetRecord.onlineAttendanceDetailDto[itemIdx];

      targetItem.onlineAttendanceState = newState;

      const body = {
        name: targetRecord.name,
        userNumber: userNumber,
        attendancePersonalInfoDto: [
          {
            attendanceState: newState,
            moduleNumber: targetRecord.moduleNumber,
            contentNumber: targetItem.contentNumber,
            date: targetItem.viewTime,
          },
        ],
      };

      mutation.mutate(body);

      return copy;
    });
  };

  const attendanceStateMapper = {
    ATTENDANCE: '출석',
    ABSENCE: '결석',
    LATE: '지각',
    EXCUSED_ABSENCE: '공결',
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  // 정렬된 데이터 생성
  const attendanceRows = useMemo(() => {
    if (!updatedAttendanceStates) return { flat: false, rows: [] };

    const dir = sortConfig.direction === 'asc' ? 1 : -1;

    // 참여현황 정렬: 주차(모듈)별 미완료 개수로 정렬
    if (sortConfig.key === 'onlineAttendanceState') {
      const modules = [...updatedAttendanceStates];
      modules.sort((a, b) => {
        const aAbsence = a.onlineAttendanceDetailDto.filter((d) => d.onlineAttendanceState !== 'ATTENDANCE').length;
        const bAbsence = b.onlineAttendanceDetailDto.filter((d) => d.onlineAttendanceState !== 'ATTENDANCE').length;
        return (aAbsence - bAbsence) * dir;
      });
      return { flat: false, rows: modules };
    }

    // 그 외: 주차(모듈) 그룹 정렬
    const modules = [...updatedAttendanceStates];
    if (sortConfig.key === 'moduleNumber') {
      modules.sort((a, b) => (Number(a.moduleNumber) - Number(b.moduleNumber)) * dir);
    } else {
      // 주차 기본 오름차순
      modules.sort((a, b) => Number(a.moduleNumber) - Number(b.moduleNumber));
    }

    const groupedRows = modules.map((record) => {
      const details = [...record.onlineAttendanceDetailDto];
      switch (sortConfig.key) {
        case 'title':
          details.sort((a, b) => (a.title || '').localeCompare(b.title || '') * dir);
          break;
        case 'category':
          details.sort((a, b) => (a.category || '').localeCompare(b.category || '') * dir);
          break;
        case 'submittedAt':
          details.sort((a, b) => {
            const av = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
            const bv = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
            return (av - bv) * dir;
          });
          break;
        case 'pointsPossible':
          details.sort((a, b) => (Number(a.pointsPossible ?? 0) - Number(b.pointsPossible ?? 0)) * dir);
          break;
        case 'score':
          details.sort((a, b) => (Number(a.score ?? 0) - Number(b.score ?? 0)) * dir);
          break;
        default:
          // moduleNumber 정렬 외에는 기본 차시 순서 유지
          break;
      }

      return { ...record, onlineAttendanceDetailDto: details };
    });

    return { flat: false, rows: groupedRows };
  }, [updatedAttendanceStates, sortConfig]);

  const sortArrow = (key) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '');

  const renderItemCells = (item, isLast = true) => {
    const borderClass = `align-middle text-center ${isLast ? '' : 'border-none'}`;
    return (
      <>
        <td className={borderClass}>
          <div className='d-flex justify-content-start align-items-center'>
            <div>{item.title}</div>
          </div>
        </td>
        <td className={borderClass}>
          <div className='d-flex justify-content-center align-items-center'>
            <span className='mr-5'>{item.category}</span>
            {item.category === '과제' ? (
              <img src='/assets/img/icon/assignments_icon.png' alt='Announcements Icon' className='panopto-icon-img cursor-pointer'
                onClick={() => handleGoingLecture(`/courses/${courseId}/gradebook/speed_grader?assignment_id=${item.assignmentId}&student_id=${userLmsNumber}`)} />
            ) : item.category === '퀴즈' ? (
              <img src='/assets/img/icon/quiz_icon.png' alt='Quiz Icon' className='panopto-icon-img cursor-pointer'
                onClick={() => handleGoingLecture(`/courses/${courseId}/gradebook/speed_grader?assignment_id=${item.assignmentId}&student_id=${userLmsNumber}`)} />
            ) : item.category === '동영상' ? (
              <img src='/assets/img/icon/external_icon.png' alt='External Icon' className='panopto-icon-img cursor-pointer'
                onClick={() => handleGoingLecture(`/courses/${courseId}/assignments/${item.assignmentId}`)} />
            ) : ''}
          </div>
        </td>
        <td className={borderClass}>{item.submittedAt}</td>
        <td className={borderClass}>
          {item.onlineAttendanceState === 'ATTENDANCE' ? (
            <div className='d-flex align-items-center justify-content-center'>
              <div className='d-flex justify-content-center align-items-center py-1 px-2 rounded-5 body-sub'
                style={{ background: '#F8F9FA', color: '#0D6EFD', width: '80px', height: '40px' }}>
                완료
              </div>
            </div>
          ) : (
            <div className='d-flex align-items-center justify-content-center'>
              <div className='d-flex justify-content-center align-items-center py-1 px-2 text-white rounded-5 body-sub'
                style={{ background: '#6C757D', width: '80px', height: '40px' }}>
                미완료
              </div>
            </div>
          )}
        </td>
        {type === 'grade' && (
          <>
            <td className={borderClass}>{item.pointsPossible}</td>
            <td className={borderClass}>{item.score}</td>
          </>
        )}
      </>
    );
  };

console.log('attendanceRows: ', attendanceRows);

  return (
    <>
      <div
        onClick={closePopup}
        className='backdrop w-100 h-100 position-fixed top-0 start-0'
        style={{ zIndex: '550' }}
      ></div>
      <div
        className='bg-white position-fixed position-center p-4 rounded-2 popupContainer'
        style={{
          width: '90vw',
          maxWidth: '1200px',
          maxHeight: '85vh',
          marginTop: '35px',
          overflowY: 'auto',
          zIndex: '600',
        }}
      >
        <div className='d-flex align-items-center justify-content-between'>
          <h3 className='h2 text-gray1 fw-bold py-2 mb-3'>{type === 'grade' ? '성적' : '출석'} 상세 현황</h3>
          <CloseButton onClick={closePopup} style={{ fontSize: '24px' }} />
        </div>

        {/* 학생 정보 */}
        <div className='border border-start-0 border-end-0'>
          <ul className='py-3'>
            <li className='d-flex flex-wrap'>
              <h4 className='fw-bold me-4 my-2'>학번:</h4>
              <span className='my-2 me-5'>{userNumber}</span>
              <h4 className='fw-bold me-4 my-2'>이름:</h4>
              <span className='my-2 me-5'>{userName}</span>
              <h4 className='fw-bold me-4 my-2'>출석:</h4>
              <span className='my-2 me-5'>
                <span style={{ color: '#61BF5E' }}>{attendanceCnt ?? 0}</span>
                {' / '}
                <span style={{ color: '#D92B2B' }}>{absenceCnt ?? 0}</span>
              </span>
              {type === 'grade' ? (
                <>
                  <h4 className='fw-bold me-4 my-2'>총 점수:</h4>
                  <span className='my-2 me-5'>{totalScore}</span>
                  <h4 className='fw-bold me-4 my-2'>최종 등급:</h4>

                  <span className='my-2 me-5'>
                    <IsPass completed={finalGrade} />
                  </span>
                </>
              ) : (
                ''
              )}
            </li>
          </ul>
        </div>
        {/* 테이블 */}
        <Table>
          <thead className='fw-bold border border-2 border-start-0 border-end-0 border-top-0'>
            <tr className='text-center'>
              <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('moduleNumber')}>
                주차{sortArrow('moduleNumber')}
              </th>
              <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('title')}>
                차시{sortArrow('title')}
              </th>
              <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('category')}>
                카테고리{sortArrow('category')}
              </th>
              <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('submittedAt')}>
                참여완료일자{sortArrow('submittedAt')}
              </th>
              <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('onlineAttendanceState')}>
                참여현황{sortArrow('onlineAttendanceState')}
              </th>
              {type === 'grade' ? (
                <>
                  <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('pointsPossible')}>
                    배점{sortArrow('pointsPossible')}
                  </th>
                  <th style={{ padding: '20px', cursor: 'pointer' }} onClick={() => handleSort('score')}>
                    점수{sortArrow('score')}
                  </th>
                </>
              ) : (
                ''
              )}
            </tr>
          </thead>
          <tbody>
            {attendanceRows.flat
              ? // 참여현황 정렬: flat 렌더링 (rowSpan 없이 각 행에 주차 표시)
                attendanceRows.rows.map(({ moduleNumber, item }, index) => (
                  <tr key={`flat-${index}`}>
                    <td className='CHA_LIGHT align-middle text-center' style={{ backgroundColor: '#EAF2FF' }}>
                      {moduleNumber}
                    </td>
                    {renderItemCells(item)}
                  </tr>
                ))
              : // 그 외 정렬: 주차별 그룹 렌더링 (rowSpan)
                attendanceRows.rows.map((record) =>
                  record.onlineAttendanceDetailDto.map((item, itemIndex) => {
                    const lastIndex = record.onlineAttendanceDetailDto.length - 1;
                    return (
                      <tr key={`${record.moduleNumber}-${itemIndex}`}>
                        {itemIndex === 0 && (
                          <td
                            rowSpan={record.onlineAttendanceDetailDto.length}
                            className='CHA_LIGHT align-middle text-center'
                            style={{ backgroundColor: '#EAF2FF' }}
                          >
                            {record.moduleNumber}
                          </td>
                        )}
                        {renderItemCells(item, lastIndex === itemIndex)}
                      </tr>
                    );
                  })
                )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default OnlineAttendancePopup;
